import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@/fontawesome';
import Modal from '@/components/ui/Modal';
import { APIRoutes } from '@/services/api.routes';
import { handleAPIError } from '@/utils/toasts';
import type { DayTradingSessionID, DayTradingChatMessageID } from '@/models/ids';
import type { DayTradingSession } from '@/models/day-trading-session';

interface DeleteChatMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: DayTradingSessionID;
  messageId: DayTradingChatMessageID;
  onDeleted: (session: DayTradingSession) => void;
}

export default function DeleteChatMessageModal({
  isOpen,
  onClose,
  sessionId,
  messageId,
  onDeleted,
}: DeleteChatMessageModalProps) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const session = await APIRoutes.deleteDayTradingChatMessage(sessionId, messageId);
      onDeleted(session);
      onClose();
    } catch (err) {
      handleAPIError(err, 'Fehler beim Löschen der Nachricht');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Nachricht löschen"
      icon={<FontAwesomeIcon icon={faTrash} className="w-5 h-5 text-red-600" />}
      maxWidth="sm"
      closeDisabled={deleting}
    >
      <div className="p-6 space-y-4">
        <p className="text-gray-700 dark:text-gray-300">
          Möchtest du diese Nachricht wirklich löschen?
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={deleting}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 cursor-pointer transition-colors disabled:opacity-50"
          >
            {deleting ? 'Lösche…' : 'Löschen'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
